import type { FC, MouseEventHandler } from 'react';
import type React from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { BLOCK_ACTIONS_HOVERED_Z_INDEX, BLOCK_ACTIONS_Z_INDEX } from '@constants/z-indices';
import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import { autoUpdate, limitShift, shift, useFloating, type VirtualElement } from '@floating-ui/react';
import { isEndorsementViewPath } from '@utils/app-paths';
import clsx from 'clsx';

import { SummaryBlockActionsView } from '../views/SummaryBlockActionsView';

import { ContractBlockActionsController } from './ContractBlockActionsController';
import { EndorsementBlockActionsController } from './EndorsementBlockActionsController';

const spanAllSegments = (firstSegment: HTMLElement, blockId: string): VirtualElement => ({
  contextElement: firstSegment,
  getBoundingClientRect: () => {
    const rect = firstSegment.getBoundingClientRect();
    const segments = document.querySelectorAll(`[data-test-block-id="${blockId}"]`);
    if (segments.length < 2) return rect;
    const { bottom } = segments[segments.length - 1].getBoundingClientRect();
    return new DOMRect(rect.x, rect.y, rect.width, bottom - rect.y);
  },
});

interface BlockActionsControllerProps {
  handleDelete: MouseEventHandler;
  handleEdit: MouseEventHandler<HTMLDivElement>;
  blockContentRef?: React.RefObject<HTMLDivElement | null>;
}

export const BlockActionsController: FC<BlockActionsControllerProps> = ({
  handleDelete,
  handleEdit,
  blockContentRef,
}) => {
  'use memo';

  const {
    block,
    segment,
    details: { isSummaryBlock },
  } = useBlockFlags();
  const firstSegment = blockContentRef?.current;

  const { refs, floatingStyles } = useFloating({
    placement: 'right-start',
    middleware: [
      shift({
        crossAxis: false,
        limiter: limitShift({
          offset: ({ rects }) => rects.floating.height,
        }),
      }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: firstSegment && segment ? spanAllSegments(firstSegment, block.id) : firstSegment,
    },
  });

  const isInEditView = useBlockEditFormStore(({ formValues }) => formValues?.id !== undefined);
  const isAnyMultiSelectActive = useMultiSelectStore(
    (state) => state.activePartId !== null && state.selectedBlockIds.size > 0,
  );
  const isEndorsementView = isEndorsementViewPath();

  // CSS-hide, never unmount: edit-mode flips toggle this for every block at once, and remounting
  // N floating menus (floating-ui autoUpdate + tooltips) blocks the main thread on large contracts.
  // Must be display:none, not visibility: comment icons set `visible`/`pointer-events-auto` on
  // themselves (alwaysVisible), which would override an ancestor's visibility:hidden.
  const isHidden = isInEditView || isAnyMultiSelectActive;

  return (
    <div
      aria-hidden={isHidden}
      className={clsx('py-1', BLOCK_ACTIONS_Z_INDEX, BLOCK_ACTIONS_HOVERED_Z_INDEX, isHidden && 'hidden')}
      data-testid={`block-actions:${block.id}`}
      ref={refs.setFloating}
      style={floatingStyles}
    >
      <ShowFirstMatching>
        <Show when={isSummaryBlock && isEndorsementView}>
          <SummaryBlockActionsView block={block} handleDelete={handleDelete} handleEdit={handleEdit} />
        </Show>
        <Show when={isEndorsementView}>
          <EndorsementBlockActionsController block={block} />
        </Show>
        <Show when={true}>
          <ContractBlockActionsController block={block} handleDelete={handleDelete} handleEdit={handleEdit} />
        </Show>
      </ShowFirstMatching>
    </div>
  );
};
